import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';

// Set to true once we've made at least one /auth/me probe in this tab, so we
// don't hit the backend on every navigation.
let sessionProbed = false;

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // ── Auth routes (public) ──
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true, layout: 'auth' },
    },
    {
      path: '/login/mfa',
      name: 'login-mfa',
      component: () => import('@/views/MfaChallengeView.vue'),
      meta: { public: true, layout: 'auth' },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/RegisterView.vue'),
      meta: { public: true, layout: 'auth' },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { public: true, layout: 'auth' },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { public: true, layout: 'auth' },
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('@/views/VerifyEmailView.vue'),
      meta: { public: true, layout: 'auth' },
    },
    // ── Protected routes ──
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
    },
    {
      path: '/mitre-matrix',
      name: 'mitre-matrix',
      component: () => import('@/views/MitreMatrixView.vue'),
    },
    {
      path: '/attack-chain',
      name: 'attack-chain',
      component: () => import('@/views/AttackChainView.vue'),
    },
    {
      path: '/detection-rules',
      name: 'detection-rules',
      component: () => import('@/views/DetectionRulesView.vue'),
    },
    {
      path: '/campaigns',
      name: 'campaigns',
      component: () => import('@/views/CampaignsView.vue'),
    },
    {
      path: '/indicators',
      name: 'indicators',
      component: () => import('@/views/IndicatorsView.vue'),
    },
    {
      path: '/reports',
      name: 'reports',
      component: () => import('@/views/ReportsView.vue'),
    },
    {
      path: '/reports/:id',
      name: 'report-detail',
      component: () => import('@/views/ReportView.vue'),
    },
    {
      path: '/reports/:id/edit',
      name: 'report-edit',
      component: () => import('@/views/ReportEditView.vue'),
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
    },
    {
      path: '/settings/mfa',
      name: 'mfa-setup',
      component: () => import('@/views/MfaSetupView.vue'),
    },
    {
      path: '/import',
      name: 'import',
      component: () => import('@/views/ImportView.vue'),
    },
    // ── Accept invitation (public) ──
    {
      path: '/accept-invite',
      name: 'accept-invite',
      component: () => import('@/views/AcceptInviteView.vue'),
      meta: { public: true, layout: 'auth' },
    },
    {
      // P2b — public pricing page is the SSOT for tier-boundary copy.
      // Every free-tier upgrade tooltip in the product links here.
      path: '/pricing',
      name: 'pricing',
      component: () => import('@/views/PricingView.vue'),
      meta: { public: true, layout: 'minimal' },
    },
    // ── Admin routes (super_admin only) ──
    {
      path: '/admin/threats',
      name: 'admin-threats',
      component: () => import('@/views/admin/AdminThreatsView.vue'),
      meta: { requiresRole: 'super_admin' },
    },
    {
      path: '/admin/customers',
      name: 'admin-customers',
      component: () => import('@/views/admin/AdminCustomersView.vue'),
      meta: { requiresRole: 'super_admin' },
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: () => import('@/views/admin/AdminUsersView.vue'),
      meta: { requiresRole: 'super_admin' },
    },
    {
      path: '/admin/rankings',
      name: 'admin-rankings',
      component: () => import('@/views/admin/AdminRankingsView.vue'),
      meta: { requiresRole: 'super_admin' },
    },
    {
      path: '/admin/detection-rules',
      name: 'admin-detection-rules',
      component: () => import('@/views/admin/AdminDetectionRulesView.vue'),
      meta: { requiresRole: 'super_admin' },
    },
    {
      path: '/admin/reports',
      name: 'admin-reports',
      component: () => import('@/views/admin/AdminReportsView.vue'),
      meta: { requiresRole: 'super_admin' },
    },
    {
      path: '/admin/campaigns',
      name: 'admin-campaigns',
      component: () => import('@/views/admin/AdminCampaignsView.vue'),
      meta: { requiresRole: 'super_admin' },
    },
    {
      path: '/admin/indicators',
      name: 'admin-indicators',
      component: () => import('@/views/admin/AdminIndicatorsView.vue'),
      meta: { requiresRole: 'super_admin' },
    },
    {
      path: '/admin/collab',
      name: 'admin-collab',
      component: () => import('@/views/admin/AdminCollabView.vue'),
      meta: { requiresRole: 'super_admin' },
    },
    {
      path: '/admin/mitre',
      name: 'admin-mitre',
      component: () => import('@/views/admin/AdminMitreView.vue'),
      meta: { requiresRole: 'super_admin' },
    },
    {
      path: '/admin/scoring',
      name: 'admin-scoring',
      component: () => import('@/views/admin/AdminScoringView.vue'),
      meta: { requiresRole: 'super_admin' },
    },
  ],
});

// Navigation guard — redirect unauthenticated users to login.
router.beforeEach(async (to) => {
  const authStore = useAuthStore();

  // On first navigation in this tab, try to restore the session from the
  // httpOnly auth cookie (if any). This runs on refresh and on deep-links.
  if (!sessionProbed && !authStore.isAuthenticated) {
    sessionProbed = true;
    await authStore.fetchMe();
  }

  // Public routes don't require authentication.
  if (to.meta.public) {
    if (authStore.isAuthenticated) {
      return { name: 'dashboard' };
    }
    return true;
  }

  // Protected route — if not logged in, bounce to login preserving destination.
  if (!authStore.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  // Role-based access check.
  const requiredRole = to.meta.requiresRole as string | undefined;
  if (requiredRole && authStore.user?.role !== requiredRole) {
    return { name: 'dashboard' };
  }

  return true;
});

export default router;
